import { inject, Injectable } from "@angular/core";
import { CartStateService } from "../../../store/state/order/orderv2/workflows/order-cart/order-cart.state.service";
import { CartData, SubmitCartContent, SubmitCartData } from "../../../models/order/workflow/submit-cart.model";
import { OrderPayloadHelperService } from "../order-payload-helper.service";
import { OrderCartData } from "../../../models/order/workflow/order-cart.model";
import { OrderService } from "../order.service";
import { throwError } from "rxjs";

@Injectable({ providedIn: 'root' })
export class SubmitCartService {
    #orderPayloadHelperService = inject(OrderPayloadHelperService)
    #orderService = inject(OrderService)

    private cartStateService = inject(CartStateService);

    submitCart() {
        const submitCartData = this.prepareSubmitCartData();
        if (!submitCartData) {
            return throwError(() => new Error('Cart data is not available. Cannot submit cart.'));
        }
        return this.#orderService.submitCartForTPV(submitCartData);
    }

    private prepareSubmitCartData(): SubmitCartData | null {
        const cart = this.cartStateService.cart();
        if (!cart) {
            return null;
        }
        const { OrderNumber } = this.#orderPayloadHelperService.getOrderBasicInfo();
        const submitCartContext: SubmitCartContent = {
            stiOrderNum: OrderNumber || '',
            cartJSON: this.getSubmitCartFromCart(cart),
        };

        const result: SubmitCartData = {
            showAll: false,
            data: submitCartContext
        };
        return result;
    }

    private getSubmitCartFromCart(orderCartData: OrderCartData): CartData {
        if (!orderCartData) {
            return { cartObjects: [] };
        }
        const cartObjects = [];
        // Process first bill charges
        for (const item of orderCartData.firstBillCharges.items) {
            cartObjects.push({
                section: 'FirstBill',
                subSection: '',
                name: item?.name,
                cartDisclosure: item?.disclosure || '',
                cartDisclosureArray: '',
                amount: item?.amount?.toString()
            });
        }

        // Process today's charges
        for (const item of orderCartData.todaysCharges.items) {
            cartObjects.push({
                section: 'Today',
                subSection: '',
                name: item?.name,
                cartDisclosure: item?.disclosure || '',
                cartDisclosureArray: '',
                amount: item?.amount?.toString()
            });
        }

        //Process monthly items
        for (const item of orderCartData.packagesCharges.items) {
            cartObjects.push({
                section: 'Package',
                subSection: '',
                name: item?.name,
                cartDisclosure: item?.disclosure || '',
                cartDisclosureArray: '',
                amount: item?.amount?.toString()
            });
        }

        //internet charges
        if (orderCartData.internetCharges) {

            cartObjects.push({
                section: 'Internet',
                subSection: '',
                name: orderCartData.internetCharges.name,
                cartDisclosure: '',
                cartDisclosureArray: '',
                amount: orderCartData.internetCharges.price.toString()
            });

            for (const item of orderCartData.internetCharges.monthly?.items || []) {
                cartObjects.push({
                    section: 'Internet',
                    subSection: '',
                    name: item?.name,
                    cartDisclosure: item?.disclosure || '',
                    cartDisclosureArray: '',
                    amount: item?.amount?.toString()
                });
            }

            for (const item of orderCartData.internetCharges.monthly?.promotions?.items || []) {
                cartObjects.push({
                    section: 'Internet',
                    subSection: 'Savings and Promotions',
                    name: item?.name,
                    cartDisclosure: item?.disclosure || '',
                    cartDisclosureArray: '',
                    amount: item?.amount?.toString()
                });
            }

            for (const item of orderCartData.internetCharges.savingAndPromotionCharges?.items || []) {
                cartObjects.push({
                    section: 'Internet',
                    subSection: 'Savings and Promotions',
                    name: item?.name,
                    cartDisclosure: item?.disclosure || '',
                    cartDisclosureArray: '',
                    amount: item?.amount?.toString()
                });
            }
        }

        //wireless charges
        if (orderCartData.wirelessCharges) {
            for (const item of orderCartData.wirelessCharges.monthly || []) {
                const subSection = item.lineNumber === 1 ? `Line ${item.lineNumber} (Primary)` : `Line ${item.lineNumber}`;
                if (item?.device) {
                    const deviceAmount = `$${item?.device.amount?.toString()}`;
                    const deviceName = `${item?.device.name} (${deviceAmount})`;
                    cartObjects.push({
                        section: 'Wireless',
                        subSection: subSection,
                        name: deviceName,
                        cartDisclosure: item?.device.disclosure || '',
                        cartDisclosureArray: '',
                        amount: ''
                    });
                }
                item.charges?.items.forEach(chargeItem => {
                    cartObjects.push({
                        section: 'Wireless',
                        subSection: subSection,
                        name: chargeItem?.name,
                        cartDisclosure: chargeItem?.disclosure || '',
                        cartDisclosureArray: '',
                        amount: chargeItem?.amount?.toString()
                    });
                });

                item.promotions?.items.forEach(promoItem => {
                    cartObjects.push({
                        section: 'Wireless',
                        subSection: subSection,
                        name: promoItem?.name,
                        cartDisclosure: promoItem?.disclosure || '',
                        cartDisclosureArray: '',
                        amount: promoItem?.amount?.toString()
                    });
                });
            }
        }

        //video charges
        if (orderCartData.videoCharges) {

            cartObjects.push({
                section: 'Video',
                subSection: '',
                name: orderCartData.videoCharges.name,
                cartDisclosure: '',
                cartDisclosureArray: '',
                amount: orderCartData.videoCharges.price.toString()
            });

            for (const item of orderCartData.videoCharges.monthly?.items || []) {
                cartObjects.push({
                    section: 'Video',
                    subSection: '',
                    name: item?.name,
                    cartDisclosure: item?.disclosure || '',
                    cartDisclosureArray: '',
                    amount: item?.amount?.toString()
                });
            }

            for (const item of orderCartData.videoCharges.monthly?.promotions?.items || []) {
                cartObjects.push({
                    section: 'Video',
                    subSection: 'Savings and Promotions',
                    name: item?.name,
                    cartDisclosure: item?.disclosure || '',
                    cartDisclosureArray: '',
                    amount: item?.amount?.toString()
                });
            }

            for (const item of orderCartData.videoCharges.savingAndPromotionCharges?.items || []) {
                cartObjects.push({
                    section: 'Video',
                    subSection: 'Savings and Promotions',
                    name: item?.name,
                    cartDisclosure: item?.disclosure || '',
                    cartDisclosureArray: '',
                    amount: item?.amount?.toString()
                });
            }
        }

        //Voice charges
        if (orderCartData.voiceCharges) {
            cartObjects.push({
                section: 'Voice',
                subSection: '',
                name: orderCartData.voiceCharges.name,
                cartDisclosure: '',
                cartDisclosureArray: '',
                amount: orderCartData.voiceCharges.price.toString()
            });

            for (const item of orderCartData.voiceCharges.monthly?.items || []) {
                cartObjects.push({
                    section: 'Voice',
                    subSection: '',
                    name: item?.name,
                    cartDisclosure: item?.disclosure || '',
                    cartDisclosureArray: '',
                    amount: item?.amount?.toString()
                });
            }

            for (const item of orderCartData.voiceCharges.monthly?.promotions?.items || []) {
                cartObjects.push({
                    section: 'Voice',
                    subSection: 'Savings and Promotions',
                    name: item?.name,
                    cartDisclosure: item?.disclosure || '',
                    cartDisclosureArray: '',
                    amount: item?.amount?.toString()
                });
            }

            for (const item of orderCartData.voiceCharges.savingAndPromotionCharges?.items || []) {
                cartObjects.push({
                    section: 'Voice',
                    subSection: 'Savings and Promotions',
                    name: item?.name,
                    cartDisclosure: item?.disclosure || '',
                    cartDisclosureArray: '',
                    amount: item?.amount?.toString()
                });
            }
        }

        return { cartObjects };
    }
}

